import { yupResolver } from '@hookform/resolvers/yup';
import type { RetailerRegionView } from '@vizit/api';
import { Button, ButtonLevel, SelectedFileStatus, Text, TextSize, TextWeight } from '@vizit/atoms';
import { UNIVERSAL_BENCHMARK_NAME } from '@vizit/constants';
import { FormDialog, FormDropZone, FormSelect, FormTextField } from '@vizit/forms';
import { InfoCircleIcon, PlusIcon } from '@vizit/icons';
import { SelectItem } from '@vizit/molecules';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import * as yup from 'yup';
import { FormProductCategoryComboBox, type FormProductCategoryComboBoxProps } from '../form-product-category-combo-box';
import { PLANNED_RETAILER_REQUIRED_MESSAGE, plannedRetailerSchema } from './planned-retailer-schema';

const SUBMISSION_ERROR_MESSAGE = 'Error creating new product. Please try again in a few minutes.';

const CATEGORY_ERROR_MESSAGES = new Map<string, string>([
  ['CATEGORY_NOT_UNIVERSAL', `This category has no ${UNIVERSAL_BENCHMARK_NAME} benchmark. Choose another category.`],
  ['CATEGORY_RETAILER_MISMATCH', 'This category is not available for the selected retailer. Choose another category.'],
]);

const formSchema = yup.object({
  file: yup.mixed<File>().required('A hero image is required'),
  retailerId: yup
    .string()
    .when('$showRetailers', ([showRetailers], s) => (showRetailers ? s.required('Retailer is required') : s)),
  category: yup.string().required('Category is required'),
  productTitle: yup.string().required('Product title is required'),
  brand: yup.string(),
  plannedRetailer: yup
    .string()
    .when(['retailerId', '$otherRetailerId'], ([retailerId, otherRetailerId], s) =>
      otherRetailerId && retailerId === otherRetailerId ? plannedRetailerSchema : s,
    ),
});

type CreateANewProductFormSchema = {
  file: File;
  retailerId?: string;
  category: string;
  productTitle: string;
  brand?: string;
  plannedRetailer?: string;
};

interface CreateANewProductSubmissionError {
  /** The API's error code, when the response carried one */
  code?: string;
}

interface CreateANewProductDialogProps {
  /**
   * Has a submission error
   *
   * @optional
   * @type {boolean}
   */
  hasOnSubmissionError?: boolean;
  /**
   * The last submission error. A new object per failed submission, so a repeated error shows again; its code
   * puts the message on the field the API refused.
   *
   * @optional
   */
  submissionError?: CreateANewProductSubmissionError | null;
  /**
   * Default values for the form
   */
  defaultValues?: CreateANewProductFormSchema;
  /**
   * Items for the retailer select dropdown
   */
  retailers?: RetailerRegionView[];
  /**
   * Id of the Other retailer; selecting it asks for the planned retailer instead of scoring against a listed one
   *
   * @optional
   */
  otherRetailerId?: string;
  /**
   * Clears the chosen category when the retailer changes, as the category list is scoped to the retailer
   *
   * @optional
   */
  clearCategoryOnRetailerChange?: boolean;
  /**
   * Query props for the product category combo box
   */
  productCategoryComboBoxProps: Omit<
    FormProductCategoryComboBoxProps<CreateANewProductFormSchema>,
    'control' | 'name' | 'label' | 'isRequired'
  >;
  /**
   * Upload status from the form to display in the dialog
   */
  uploadStatus?: SelectedFileStatus;
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * Callback to notify when the dialog open state changes
   * @param open - Whether the dialog should be open
   */
  onOpenChange?: (open: boolean) => void;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * Callback function to handle the file status change
   * @param status - The status of the file
   */
  onFileStatusChange?: (status: SelectedFileStatus) => void;
  /**
   * Notifies the parent when the user picks a different retailer in the form,
   * so the parent can re-scope the category list. Called with '' when the form reset clears the pick.
   */
  onRetailerChange?: (retailerId: string) => void;
  /**
   * Callback function to handle the create new product api call
   */
  onSubmit: (data: CreateANewProductFormSchema) => Promise<void>;
}

function CreateANewProductDialog(props: CreateANewProductDialogProps) {
  const {
    defaultValues = {
      file: undefined,
      retailerId: '',
      productTitle: '',
      brand: '',
      category: '',
      plannedRetailer: '',
    },
    hasOnSubmissionError = false,
    submissionError,
    uploadStatus,
    retailers,
    otherRetailerId,
    clearCategoryOnRetailerChange = false,
    productCategoryComboBoxProps,
    isOpen,
    onOpenChange,
    hideTrigger = false,
    triggerButtonText = 'New product page',
    useIcon = false,
    onSubmit,
    onFileStatusChange,
    onRetailerChange,
  } = props;

  const showRetailerSelect = !!retailers?.length;

  const methods = useForm<CreateANewProductFormSchema>({
    resolver: yupResolver(formSchema, {
      context: { showRetailers: showRetailerSelect },
    }) as unknown as Resolver<CreateANewProductFormSchema>,
    context: { otherRetailerId },
    defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, setError, reset, resetField, clearErrors, watch } = methods;

  const watchedRetailerId = watch('retailerId');
  const isOtherRetailerSelected = !!otherRetailerId && watchedRetailerId === otherRetailerId;

  const previousRetailerId = React.useRef(watchedRetailerId);
  React.useEffect(() => {
    const previous = previousRetailerId.current;
    previousRetailerId.current = watchedRetailerId;

    if (watchedRetailerId) {
      onRetailerChange?.(watchedRetailerId);
    } else if (previous) {
      onRetailerChange?.('');
    }

    if (previous === watchedRetailerId) return;

    if (otherRetailerId && previous === otherRetailerId) {
      resetField('plannedRetailer', { defaultValue: '' });
    }
    if (clearCategoryOnRetailerChange) {
      resetField('category', { defaultValue: '' });
    }
  }, [watchedRetailerId, onRetailerChange, otherRetailerId, clearCategoryOnRetailerChange, resetField]);

  const handleOnFileValidationError = (error?: string) => {
    if (error) {
      setError('file', { type: 'custom', message: error });
    }
  };

  const handleOnResetForm = () => {
    clearErrors();

    reset({
      file: undefined,
      retailerId: '',
      productTitle: '',
      brand: '',
      category: '',
      plannedRetailer: '',
    });

    // Reset the file status
    onFileStatusChange?.(SelectedFileStatus.Ready);
  };

  React.useEffect(() => {
    if (!hasOnSubmissionError && !submissionError) return;

    const code = submissionError?.code;
    const categoryMessage = code ? CATEGORY_ERROR_MESSAGES.get(code) : undefined;
    if (code === 'PLANNED_RETAILER_REQUIRED') {
      setError('plannedRetailer', { message: PLANNED_RETAILER_REQUIRED_MESSAGE });
    } else if (categoryMessage) {
      setError('category', { message: categoryMessage });
    } else {
      setError('productTitle', { message: SUBMISSION_ERROR_MESSAGE });
    }
  }, [hasOnSubmissionError, submissionError, setError]);

  return (
    <FormDialog
      formName="create-new-product"
      formMethods={methods}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
      data-testid="create-product-dialog"
    >
      {!hideTrigger && (
        <FormDialog.Trigger>
          <Button level={ButtonLevel.Primary} aria-label={triggerButtonText}>
            {useIcon ? <PlusIcon size={20} /> : triggerButtonText}
          </Button>
        </FormDialog.Trigger>
      )}
      <FormDialog.Title>Create new product page</FormDialog.Title>
      <FormDialog.Subtitle>
        Enter the information for your new product page and upload images to get started.
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        {!!retailers?.length && (
          <FormSelect control={control} name="retailerId" label="Retailer" placeholder="Choose retailer" isRequired>
            {retailers.map((item) => (
              <SelectItem key={item.id} id={item.id} textValue={item.display_name}>
                {item.display_name}
              </SelectItem>
            ))}
          </FormSelect>
        )}
        {isOtherRetailerSelected && (
          <div className="mb-10 flex flex-col gap-6">
            <FormTextField
              control={control}
              name="plannedRetailer"
              label="Which retailer are you planning to use this for?"
              placeholder="e.g. Costco US, Target US, Ulta"
              isRequired
              className="min-h-0"
            />
            <div
              role="note"
              className="flex items-center gap-6 rounded-none border border-indigo-600 bg-indigo-50 px-6 py-3 shadow-md"
            >
              <span className="shrink-0 text-indigo-600">
                <InfoCircleIcon size={24} />
              </span>
              <Text size={TextSize.Sm} weight={TextWeight.Medium} showEllipsis={false} className="text-quaternary">
                We'll score your page against our {UNIVERSAL_BENCHMARK_NAME} benchmark.
              </Text>
            </div>
          </div>
        )}
        <FormProductCategoryComboBox
          control={control}
          name="category"
          label="Category"
          isRequired
          menuTrigger="input"
          {...productCategoryComboBoxProps}
        />
        <FormTextField control={control} name="productTitle" label="Product title" isRequired />
        <FormTextField control={control} name="brand" label="Brand" />
        <FormDropZone
          label="Add a hero image"
          helperText="Carousel assets can be added once the product is created!"
          control={control}
          name="file"
          isRequired
          uploadStatus={uploadStatus}
          onFileValidationError={handleOnFileValidationError}
          onFileStatusChange={onFileStatusChange}
          dropZoneClassName="min-h-0"
          selectedFilesClassName="min-h-0"
        />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>Score my page!</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { CreateANewProductDialog };
export type { CreateANewProductFormSchema, CreateANewProductDialogProps, CreateANewProductSubmissionError };
